import { inject, Injectable } from "@angular/core";
import { AddConfigToProductListModalComponent } from "@components/add-config-to-product-list-modal/add-config-to-product-list-modal.component";
import { FGDS_SEARCHABLE_PRODUCT_TYPES, isRequiredAttachment, Product } from "@domain/product/product";
import { ProductService } from "@domain/product/product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { FgdsDirectBomEntry, FgdsDirectBomLine } from "@domain/project/direct-bom/fgds-direct-bom-entry";
import { FgdsProject } from "@domain/project/fgds-project";
import { ModalService } from "@odx/angular/components/modal";
import { ToastHelper } from "@utils/toast-helper.service";
import { forkJoin, map, Subject } from "rxjs";

@Injectable({ providedIn: "root" })
export class FgdsProductListAddService {
  private modalService = inject(ModalService);
  private productService = inject(ProductService);
  private toastHelper = inject(ToastHelper);

  readonly productAdded$ = new Subject<void>();

  addProductToProductList(product: Product, project: FgdsProject, name: string): void {
    if (!FGDS_SEARCHABLE_PRODUCT_TYPES.includes(product.type)) {
      this.addDirectBomEntry(product, project, name);
      return;
    }

    const existingConfig = project.findProductConfig(product.id);
    if (existingConfig) {
      this.showQuantityIncrementModal(existingConfig, name);
      return;
    }

    this.buildConfigModalRows(product).subscribe({
      next: (rows) => {
        this.modalService
          .open(AddConfigToProductListModalComponent, {
            id: "add-config-to-product-list-modal",
            data: { title: name, rows, quantity: 1 },
          })
          .onClose$.subscribe((quantity) => {
            if (typeof quantity !== "number") return;
            try {
              project.createAndAddProductConfig(product, name, quantity);
              this.toastHelper.success(
                $localize`:@@toast.productList.added.header:Zur Produktliste hinzugefügt`,
                $localize`:@@toast.productList.added.message:${name} wurde zur Produktliste hinzugefügt.`,
              );
              this.productAdded$.next();
            } catch {
              this.toastHelper.error(
                $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
                $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
              );
            }
          });
      },
      error: () => {
        this.toastHelper.error(
          $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
          $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
        );
      },
    });
  }

  private buildConfigModalRows(product: Product) {
    const attachmentIds = product.relationships.filter((rel) => isRequiredAttachment(rel)).map((rel) => rel.productId);
    const allIds = [product.id, ...attachmentIds];
    return forkJoin(
      allIds.map((id) =>
        this.productService
          .getProductNameById(id)
          .pipe(map((productName) => ({ quantity: 1, name: productName || id, partNumber: id }))),
      ),
    );
  }

  private showQuantityIncrementModal(config: ProductConfiguration, name: string): void {
    const currentQuantity = config.additionalProductListQuantity;
    this.modalService
      .open(AddConfigToProductListModalComponent, {
        id: "add-config-to-product-list-modal",
        data: { title: name, rows: [{ quantity: 1, name, partNumber: config.mainProductId }], quantity: currentQuantity + 1 },
      })
      .onClose$.subscribe((quantity) => {
        if (typeof quantity !== "number") return;
        try {
          config.setAdditionalProductListQuantity(quantity);
          this.toastHelper.success(
            $localize`:@@toast.productList.added.header:Zur Produktliste hinzugefügt`,
            $localize`:@@toast.productList.added.message:${name} wurde zur Produktliste hinzugefügt.`,
          );
          this.productAdded$.next();
        } catch {
          this.toastHelper.error(
            $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
            $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
          );
        }
      });
  }

  private addDirectBomEntry(product: Product, project: FgdsProject, name: string): void {
    const existingEntry = project.findDirectBomEntryByPartNumber(product.id);
    if (existingEntry) {
      this.showDirectEntryQuantityIncrementModal(existingEntry, project, name);
      return;
    }

    this.modalService
      .open(AddConfigToProductListModalComponent, {
        id: "add-config-to-product-list-modal",
        data: { title: name, rows: [{ quantity: 1, name, partNumber: product.id }], quantity: 1 },
      })
      .onClose$.subscribe((quantity) => {
        if (typeof quantity !== "number") return;
        try {
          project.addDirectBomEntry(
            FgdsDirectBomEntry.create(name, product.type, quantity, [
              FgdsDirectBomLine.create(product.id, name, product.type, 1),
            ]),
          );
          this.toastHelper.success(
            $localize`:@@toast.productList.added.header:Zur Produktliste hinzugefügt`,
            $localize`:@@toast.productList.added.message:${name} wurde zur Produktliste hinzugefügt.`,
          );
          this.productAdded$.next();
        } catch {
          this.toastHelper.error(
            $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
            $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
          );
        }
      });
  }

  private showDirectEntryQuantityIncrementModal(entry: FgdsDirectBomEntry, project: FgdsProject, name: string): void {
    this.modalService
      .open(AddConfigToProductListModalComponent, {
        id: "add-config-to-product-list-modal",
        data: {
          title: name,
          rows: entry.lines.map((line) => ({ quantity: line.quantity, name: line.name, partNumber: line.partNumber })),
          quantity: entry.quantity + 1,
        },
      })
      .onClose$.subscribe((quantity) => {
        if (typeof quantity !== "number") return;
        try {
          project.setDirectBomEntryQuantity(entry, quantity);
          this.toastHelper.success(
            $localize`:@@toast.productList.added.header:Zur Produktliste hinzugefügt`,
            $localize`:@@toast.productList.added.message:${name} wurde zur Produktliste hinzugefügt.`,
          );
          this.productAdded$.next();
        } catch {
          this.toastHelper.error(
            $localize`:@@toast.productList.addFailed.header:Hinzufügen fehlgeschlagen`,
            $localize`:@@toast.productList.addFailed.message:Das Produkt konnte nicht zur Produktliste hinzugefügt werden.`,
          );
        }
      });
  }
}
